/**
 * PR27 human-QA fixes: `LeaderboardScene`'s mobile layout had significant
 * overlap (controls overlapping the title/BACK button, world/level rows
 * crowding each other) because the header-to-content flow was computed
 * from `titleSize` alone (`title.y + titleSize * 0.9`) - a value that
 * shrinks on a narrow viewport, while the BACK button's own height stays
 * a fixed 40px regardless of viewport width. A follow-up QA pass then
 * found the title and BACK button could still overlap HORIZONTALLY on
 * the narrowest phones (the centered title's own text width was never
 * checked against the BACK button's zone), and asked for the world
 * selector's icon-based mobile treatment to become the ONLY world
 * selector - the desktop text-button/multi-row-wrap variant is removed
 * entirely, not just visually replaced.
 *
 * This module is the single source of truth for every region's vertical
 * position (and, for the header row, the horizontal title/BACK
 * separation too) - Phaser-free (like `worldCardLayout.ts`, for the same
 * unit-testing reason: no Phaser render harness exists in this repo), so
 * the "no region depends on accidentally fitting inside another region's
 * space" contract can be asserted directly, not just eyeballed on a
 * Deploy Preview. `LeaderboardScene` calls `computeLeaderboardRegions()`
 * once per `buildLayout()` and positions every control from its result,
 * rather than re-deriving offsets inline.
 *
 * Internally the vertical flow always works in EDGES, never centers-
 * plus-an-assumed-gap: every region's top is `previousBottom +
 * regionGap`, its bottom is `top + height`, and only its on-screen
 * center (`...Y`, what Phaser positioning actually wants) is derived
 * from those two. Computing a "next center" directly as `previousBottom
 * + gap` (without separately accounting for the next element's own
 * half-height) is exactly the class of bug this module exists to
 * eliminate - it would silently shrink the real gap by up to that
 * element's half-height, which is precisely how the original bug's
 * `titleSize`-only spacing broke down.
 */

export const LB_BACK_BUTTON_X = 64;
export const LB_BACK_BUTTON_WIDTH = 96;
export const LB_BACK_BUTTON_HEIGHT = 40;
export const LB_TAB_ROW_HEIGHT = 40;
export const LB_LEVEL_PILL_SIZE = 38;
export const LB_LEVEL_PILL_GAP = 8;
export const LB_LEVEL_PILL_HIT_SIZE = 44;

/** The one world-icon-based selector, used at every viewport width (the old desktop text-button variant is removed, not just hidden). */
export const LB_WORLD_ICON_GAP = 14;
export const LB_WORLD_ICON_SLOT_MIN = 52;
/** A generous desktop cap - past this, the row simply stays this comfortable size, centered, rather than stretching to fill an ultra-wide viewport. */
export const LB_WORLD_ICON_SLOT_MAX = 92;
/** The icon row's own max total width, independent of viewport - keeps it reading as one compact, balanced control on a wide desktop screen instead of spreading edge to edge. */
export const LB_WORLD_ICON_ROW_MAX_WIDTH = 520;

export const LB_TITLE_TEXT = "LEADERBOARD";
export const LB_TITLE_SIZE_MIN = 14;
export const LB_TITLE_SIZE_MAX = 34;
/** Clear space required between the BACK button's own right edge and the title's nearest edge - the title is free to use any size up to whatever keeps this gap intact. Only still used by `resolveTitleSize()`/`DailyChallengeScene.ts`'s single-row header - the three Leaderboard scenes use the two-row mobile header below instead. */
export const LB_HEADER_TITLE_SAFETY_GAP = 16;

/**
 * Mobile beta fix (PR29, 3rd round): below this viewport width, BACK and
 * the leaderboard title no longer share one row at all - fighting over a
 * horizontal safe zone next to BACK forced every long title
 * ("ENDLESS RANKS"/"DAILY LEADERBOARD"/"LEADERBOARD") into either a
 * shrink-toward-illegible or an ugly mid-word character-split wrap, and
 * human QA on the Deploy Preview confirmed both looked bad even once
 * neither one any longer geometrically overlapped BACK. Matches
 * `WorldSelectScene`'s own `STACK_BREAKPOINT` for the same "narrow phone"
 * threshold used elsewhere in this app.
 */
export const LB_MOBILE_HEADER_BREAKPOINT = 640;

/**
 * The world icon selector's per-icon "slot" width (icon + its own share
 * of the gap) at a given viewport width - clamped to a comfortable
 * range (never so small the touch target/icon become illegible, capped
 * so it never balloons on a very wide screen) and to `LB_WORLD_ICON_ROW_MAX_WIDTH` overall.
 */
export function worldIconSlot(viewportWidth: number, worldCount: number): number {
  const usableWidth = Math.min(viewportWidth - 32, LB_WORLD_ICON_ROW_MAX_WIDTH);
  const count = Math.max(1, worldCount);
  const raw = (usableWidth - LB_WORLD_ICON_GAP * (count - 1)) / count;
  return Math.max(LB_WORLD_ICON_SLOT_MIN, Math.min(LB_WORLD_ICON_SLOT_MAX, raw));
}

/**
 * The title's own "wants to be this size" font size before checking it
 * against the BACK button's zone (see `maxSafeTitleWidth()`/
 * `resolveTitleSize()` below) - unchanged from the original formula.
 */
export function desiredTitleSize(viewportWidth: number): number {
  return Math.max(24, Math.min(LB_TITLE_SIZE_MAX, viewportWidth * 0.06));
}

/**
 * The most a centered header title may span horizontally before it
 * starts reaching into the BACK button's own zone (plus a safety gap),
 * at this viewport width. The title is centered on the full viewport, so
 * only half of this budget is actually free on the LEFT side (where the
 * BACK button sits) - the right side has the same budget mirrored, so
 * using the smaller (left) side for both, doubled, is the correct
 * (symmetric) total width budget for a centered title.
 */
export function maxSafeTitleWidth(viewportWidth: number): number {
  const backButtonRight = LB_BACK_BUTTON_X + LB_BACK_BUTTON_WIDTH / 2;
  const safeHalfWidth = viewportWidth / 2 - (backButtonRight + LB_HEADER_TITLE_SAFETY_GAP);
  return Math.max(0, safeHalfWidth * 2);
}

/**
 * Resolves the title's ACTUAL font size given its real, measured
 * (Phaser) width at `desiredTitleSize()` - shrinks it only as far as
 * needed to fit `maxSafeTitleWidth()`, floored at `LB_TITLE_SIZE_MIN` so
 * it never becomes illegible. Takes the real measured width (not an
 * estimate) so the result is exact regardless of font metrics - callers
 * without a live Phaser Text object (e.g. this module's own tests) can
 * still exercise the clamping logic by passing a hypothetical width.
 */
export function resolveTitleSize(desiredSize: number, measuredWidthAtDesiredSize: number, viewportWidth: number): number {
  const maxWidth = maxSafeTitleWidth(viewportWidth);
  if (measuredWidthAtDesiredSize <= maxWidth || measuredWidthAtDesiredSize <= 0) return desiredSize;
  // A small extra safety margin (3%) so a single proportional correction
  // reliably lands at/under the limit even accounting for sub-pixel
  // rounding in the real re-measured text.
  const scale = (maxWidth / measuredWidthAtDesiredSize) * 0.97;
  return Math.max(LB_TITLE_SIZE_MIN, Math.floor(desiredSize * scale));
}

export interface LeaderboardHeaderLayout {
  /** `true` below `LB_MOBILE_HEADER_BREAKPOINT` - BACK and the title sit on two separate rows instead of sharing one. */
  isStacked: boolean;
  backX: number;
  backY: number;
  backWidth: number;
  backHeight: number;
  titleY: number;
  /**
   * The title's available width for its own (defensive-only) `wordWrap`
   * constraint. Stacked (mobile): the title has its OWN full-width row, so
   * this is simply the viewport minus a small side margin - generous
   * enough that none of this app's real titles ("LEADERBOARD"/
   * "ENDLESS RANKS"/"DAILY LEADERBOARD") ever actually wrap on it (real
   * canvas `measureText` confirms all three fit in one line at every
   * mobile width down to 320px). Not stacked (desktop): the same
   * `maxSafeTitleWidth()` used before - the title still shares a row with
   * BACK there.
   */
  titleMaxWidth: number;
  /** The title's own reserved height - the tallest of a generous single-line estimate and (once known) its real measured height. Exposed mainly so callers/tests can reconstruct the title's own top/bottom edge from `titleY` without duplicating this module's internal math. */
  titleHeight: number;
  /** Bottom edge of the WHOLE header (both rows on mobile, the one shared row on desktop) - where the next region (subtitle/tab row) starts from, after adding a gap. */
  headerBottom: number;
}

/**
 * Mobile beta fix (PR29, 3rd round): replaces the previous single-row
 * "shrink the title, or wrap it into the safe zone next to BACK" approach
 * for all three Leaderboard scenes (`LeaderboardScene`/
 * `EndlessLeaderboardScene`/`DailyLeaderboardScene`) - human QA confirmed
 * both looked bad on mobile even once neither one geometrically overlapped
 * BACK anymore. Below `LB_MOBILE_HEADER_BREAKPOINT`, BACK gets its own row
 * (top-left) and the title gets its own row below it, centered, with the
 * FULL content width available - no more fighting over a narrow safe zone,
 * so the title normally never needs to shrink or wrap at all. Desktop is
 * unchanged: BACK and the title still share one row, exactly as before.
 *
 * `measuredTitleHeight` (optional, defaults to a generous single-line
 * estimate - see `computeLeaderboardRegions()`'s own doc for why): the
 * title's real rendered height, once it exists, so `headerBottom` (and
 * anything measured from it) never assumes a wrap that didn't happen, or
 * under-reserves space for the rare one that did.
 */
export function computeHeaderLayout(viewportWidth: number, viewportHeight: number, measuredTitleHeight?: number): LeaderboardHeaderLayout {
  const isStacked = viewportWidth < LB_MOBILE_HEADER_BREAKPOINT;
  const titleSize = desiredTitleSize(viewportWidth);
  // Deliberately generous (never under-estimates) - see
  // `computeLeaderboardRegions()`'s own doc for the same reasoning.
  const estimatedTitleHeight = titleSize * 1.3;
  const titleHeight = Math.max(estimatedTitleHeight, measuredTitleHeight ?? 0);

  if (isStacked) {
    const backY = viewportHeight * 0.06;
    const rowGap = Math.max(12, viewportHeight * 0.015);
    const titleTop = backY + LB_BACK_BUTTON_HEIGHT / 2 + rowGap;
    return {
      isStacked: true,
      backX: LB_BACK_BUTTON_X,
      backY,
      backWidth: LB_BACK_BUTTON_WIDTH,
      backHeight: LB_BACK_BUTTON_HEIGHT,
      titleY: titleTop + titleHeight / 2,
      titleMaxWidth: Math.max(0, viewportWidth - 32),
      titleHeight,
      headerBottom: titleTop + titleHeight,
    };
  }

  const headerY = viewportHeight * 0.07;
  const headerHeight = Math.max(LB_BACK_BUTTON_HEIGHT, titleHeight);
  return {
    isStacked: false,
    backX: LB_BACK_BUTTON_X,
    backY: headerY,
    backWidth: LB_BACK_BUTTON_WIDTH,
    backHeight: LB_BACK_BUTTON_HEIGHT,
    titleY: headerY,
    titleMaxWidth: maxSafeTitleWidth(viewportWidth),
    titleHeight,
    headerBottom: headerY + headerHeight / 2,
  };
}

export interface LeaderboardRegions {
  isStacked: boolean;
  backX: number;
  backY: number;
  backWidth: number;
  backHeight: number;
  titleY: number;
  titleMaxWidth: number;
  titleHeight: number;
  /** Bottom edge of the whole header (both rows on mobile, one row on desktop) - see `computeHeaderLayout()`'s own doc. */
  headerBottom: number;
  tabRowY: number;
  tabRowBottom: number;
  /** Y of the world selector row (always the icon-based selector now, at every viewport width). */
  worldSelectorY: number;
  worldSelectorBottom: number;
  /** `null` on the WORLD tab - there is no level row to separate from the content below it. */
  levelSelectorY: number | null;
  levelSelectorBottom: number | null;
  /** Where the scrollable leaderboard content area starts - always strictly below whichever region (level selector, or world selector when there's no level row) precedes it. */
  contentTop: number;
  /** The one gap value used between every region, so spacing reads as consistent rather than a handful of independently-tuned magic numbers. */
  regionGap: number;
}

/**
 * `measuredTitleHeight` (mobile beta fix, PR29): the title's own REAL
 * rendered height, once it exists - optional and omitted by every existing
 * caller/test, which keeps their result byte-identical (see below). Passing
 * it back in (a second call, after the title exists - see
 * `LeaderboardScene.buildLayout()`) lets `headerBottom` grow to match
 * whenever the title actually needed more room than the generous
 * single-line estimate `computeHeaderLayout()` otherwise assumes, so the
 * tab row (and every region after it) still starts strictly below the
 * title's own real bottom edge.
 */
export function computeLeaderboardRegions(viewportWidth: number, viewportHeight: number, tab: "level" | "world", worldCount: number, measuredTitleHeight?: number): LeaderboardRegions {
  const header = computeHeaderLayout(viewportWidth, viewportHeight, measuredTitleHeight);
  const { headerBottom } = header;

  const regionGap = Math.max(14, viewportHeight * 0.02);

  const tabRowTop = headerBottom + regionGap;
  const tabRowY = tabRowTop + LB_TAB_ROW_HEIGHT / 2;
  const tabRowBottom = tabRowTop + LB_TAB_ROW_HEIGHT;

  const worldSelectorTop = tabRowBottom + regionGap;
  const slot = worldIconSlot(viewportWidth, worldCount);
  const worldSelectorY = worldSelectorTop + slot / 2;
  const worldSelectorBottom = worldSelectorTop + slot;

  let levelSelectorY: number | null = null;
  let levelSelectorBottom: number | null = null;
  let contentTop: number;
  if (tab === "level") {
    const levelSelectorTop = worldSelectorBottom + regionGap;
    levelSelectorY = levelSelectorTop + LB_LEVEL_PILL_SIZE / 2;
    levelSelectorBottom = levelSelectorTop + LB_LEVEL_PILL_SIZE;
    contentTop = levelSelectorBottom + regionGap;
  } else {
    contentTop = worldSelectorBottom + regionGap;
  }

  return {
    ...header,
    tabRowY,
    tabRowBottom,
    worldSelectorY,
    worldSelectorBottom,
    levelSelectorY,
    levelSelectorBottom,
    contentTop,
    regionGap,
  };
}

/**
 * Mobile beta fix (PR29 round 2): `EndlessLeaderboardScene`/
 * `DailyLeaderboardScene` each stepped to the next row with a bare
 * `y += ROW_HEIGHT` (a fixed constant), which silently assumed every row's
 * display-name text renders on exactly one line. A max-length (16-char,
 * see `NAME_MAX_LENGTH` in `NameEntryModal.ts`) display name routinely
 * measures wider than the name column's own `wordWrap` width on a narrow
 * phone, so Phaser wraps it to a second line - and the next row, still
 * placed at the fixed `y + ROW_HEIGHT`, rendered on top of that wrapped
 * second line (in `DailyLeaderboardScene` specifically, `ROW_HEIGHT` is
 * even tighter than a wrapped 2-line block's own real height).
 *
 * The fix: every row-local element (rank/name/score/badge/time) is
 * top-anchored and placed the same as before, but the NEXT row's start Y
 * is this function's result - the tallest of that element's own real,
 * measured `y + height` (which already reflects however many lines the
 * name actually wrapped to) - rather than a value that assumed one line.
 * `minRowHeight` keeps a normal (unwrapped) row's original, already-
 * comfortable spacing exactly as before - a no-op whenever nothing in the
 * row needed more room than that.
 */
export function rowContentBottom(y: number, minRowHeight: number, elementBottoms: readonly number[]): number {
  return Math.max(y + minRowHeight, ...elementBottoms);
}
